<template>
  <div class="w-full h-full accepted-type-answer-view overscroll-y-auto">
    <div class="masonry-grid" v-if="!loadingDataGroupTypeAnswer">
      <div
        v-masonry
        transition-duration="0.3s"
        item-selector=".item"
        class="masonry-content"
      >
        <div
          class="item"
          v-for="(group, index) in groupAnswers"
          :key="index"
        >
          <div
            tabindex="0"
            class="body-content"
          >
            <div class="type-answer">
              <span v-if="group.isEditable">
                {{ group.typeAnswer | profanity }}
              </span>
              <!-- do not filter presenter's answer -->
              <span v-else>
                {{ group.typeAnswer }}
              </span>
              <aha-spinner
                v-if="loading && activeGroup === index"
                class="absolute top-0 right-0"
                :variant="isTextColourLight ? 'white' : 'dark'"
              />
              <div
                class="select-box horizontal layout center center-justified"
                :class="{
                  'cursor-not-allowed': !group.isEditable,
                  'cursor-pointer': group.isEditable,
                  'bg-base-0': group.isAccepted
                }"
                v-else
                @click="handleAcceptAnswer(group, index)"
                v-tooltip="getTooltipContent(group)"
              >
                <font-awesome-icon
                  v-if="group.isAccepted && !group.isEditable"
                  class="text-green-50"
                  icon="check-circle"
                />
                <img
                  src="@/assets/images/icon/checked.svg"
                  v-if="group.isAccepted && group.isEditable"
                >
              </div>
            </div>
            <div class="team-member" v-if="group.audiences && group.audiences.length">
              <div
                class="badge badge-pill badge-primary member horizontal layout center max-w-full truncate"
                v-for="(audience, indexMember) in group.audiences"
                :style="{ 'background-color': getColor(indexMember)}"
                :key="indexMember"
              >
                <span v-if="indexMember < 10">
                  <span v-if="isShownEmojiAvatar" class="m-r-1">{{ audience.emoji }}</span>
                  <span class="font-weight-bold">
                    {{ audience.name | profanity }}
                  </span>
                </span>
              </div>
              <div
                class="badge badge-pill member horizontal layout center bg-secondary-40"
                v-if="group.total > 10"
              >
                <span class="font-weight-bold">+{{ group.total - 10 }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="loadingDataGroupTypeAnswer"
      class="horizontal layout center center-justified full-size"
    >
      <div class="text-center">
        <div class="quiz--result">
          {{ $t("loadingResult") }}...
        </div>
        <Loading style="margin: 0 auto" />
      </div>
    </div>
  </div>
</template>
<script>
import { mapState, mapGetters } from 'vuex'
import * as Sentry from '@sentry/vue'
import Util from '@/util'
import AnswerApi from '@/api/answer'
import Loading from '@/components/Loading'
import Socket from '@/socket'

export default {
  components: {
    Loading,
  },
  data() {
    return {
      loading: false,
      groupAnswers: [],
      loadingDataGroupTypeAnswer: true,
      activeGroup: null,
    }
  },

  computed: {
    ...mapState({
      slideActive: (state) => state.slideActive,
    }),
    ...mapGetters(['isTextColourLight']),
    ...mapGetters('presentationSetting', ['isShownEmojiAvatar']),
  },

  mounted() {
    this._getData()
  },

  methods: {
    getTooltipContent(group) {
      if (!group.isEditable) return ''
      return group.isAccepted ? this.$i18nPresenter.t('Unaccept this answer') : this.$i18nPresenter.t('Accept this answer')
    },

    async handleAcceptAnswer(group, index) {
      if ((this.activeGroup === index && this.loading) || !group.isEditable) return
      this.activeGroup = index

      this.loading = true
      const { typeAnswer, isAccepted } = group
      try {
        const { accepted } = await AnswerApi.acceptTypeAnswerGroup(
          this.slideActive.id, {
            answer: typeAnswer,
            accepted: !isAccepted,
            socketId: Socket.getSocketId(),
          },
        )
        this.groupAnswers.splice(index, 1, { ...this.groupAnswers[index], isAccepted: accepted })
        this.addOrRemoveOtherAcceptedAnswers(accepted, typeAnswer)
      } catch (error) {
        this.$store.state.isSyncing = 3
        Sentry.captureException(error)
      }
      this.loading = false
    },

    addOrRemoveOtherAcceptedAnswers(accepted, title) {
      const existingAnswerIndex = this.$store.state.slideActive.otherCorrectQuiz.findIndex(
        (item) => item.title === title,
      )
      if (!accepted && existingAnswerIndex > -1) {
        this.$store.state.slideActive.otherCorrectQuiz.splice(existingAnswerIndex, 1)
      }
      if (accepted && existingAnswerIndex < 0) {
        this.$store.state.slideActive.otherCorrectQuiz.push({
          index: this.$store.state.slideActive.otherCorrectQuiz.length + 1,
          id: Math.random().toString(36).substring(7),
          accepted: true,
          title,
        })
      }
      const index = this.$store.state.presentation.slides.findIndex((item) => {
        return item.id === this.$store.state.slideActive.id
      })
      if (index !== -1) {
        this.$store.state.presentation.slides[index].otherCorrectQuiz =
          this.$store.state.slideActive.otherCorrectQuiz
      }
    },
    getColor(index) {
      return Util.getAhaColor(index)
    },
    _getBgColor() {
      const color = Util.hexToRgba(this.$store.state.slideActive.baseColour)
      return Util.changeRgbaColor(color, 0.6)
    },
    async _getData() {
      try {
        const result =
          await AnswerApi.getGroupTypeAnswer({ slideId: this.$store.state.slideActive.id })
        this.loadingDataGroupTypeAnswer = false
        this.groupAnswers = result
        setTimeout(() => {
          this.$redrawVueMasonry()
        }, 500)
      } catch (error) {
        this.$store.state.isSyncing = 3
        Sentry.captureException(error)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/variable.scss';
.masonry-grid {
  width: 100%;
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  -ms-overflow-style: auto;
  -webkit-overflow-scrolling: touch;
  position: relative;
  scrollbar-width: none;
  @apply px-12;

  &::-webkit-scrollbar {
    display: none;
  }

  .masonry-content {
    width: 100%;
    height: 100%;
  }

  .item {
    padding: 10px;
    width: calc(100% / 3);
    z-index: 9;

    .body-content {
      border: 2px solid #D3D7E1;
      border-radius: 8px;
      transition: all 0.25s;
      word-break: break-word;
      position: relative;
      @apply px-6 py-4;
      &:hover {
        transform: scale(1.05);
        .remove-icon {
          opacity: 0.5;
          &:hover {
            opacity: 1;
          }
        }
      }

      .member {
        color: #fff;
        padding: 12px;
        margin-right: 4px;
        display: inline-block;
        margin-bottom: 4px;
        font-size: 16px;
      }

      &.active, &:focus {
        @apply border-green-50;
      }
    }
    &:hover {
      z-index: 20;
    }
  }
}
.type-answer {
  font-size: 24px;
  font-weight: 600;
  line-height: 24px;
  padding-right: 36px;
  @apply mb-4 relative;
}
.select-box {
  width: 30px;
  height: 30px;
  border: 1px solid #D3D7E1;
  border-radius: 50%;
  text-align: center;
  @apply absolute top-0 right-0;
}
</style>
